// ============================================
// CurrencyProvider — tiền tệ hiển thị theo PHẠM VI đang xem (sheet 2 #75)
// ============================================
//
// Vấn đề: ~25 component hiển thị tiền là component THUẦN (bảng, thẻ KPI,
// tooltip chart) nằm sâu nhiều tầng. Truyền `currency` xuống bằng prop là phải
// sửa cả trang cha lẫn component, dễ sót — mà sót thì hiện sai ký hiệu chứ
// không báo lỗi, đúng kiểu bug #129/#160 cũ.
//
// Cách làm: một context ở gốc app giữ tiền tệ của phạm vi đang xem. Component
// gọi `useMoney()` để lấy formatter ĐÃ BIND sẵn currency, không cần biết trang
// cha là ai.
//
// ⚠️ RỦI RO ĐÃ BIẾT: trang quên set scope sẽ hiển thị tiền tệ của trang trước.
// Hai lớp chặn:
//   1. `useCurrencyScope()` TỰ RESET về mặc định khi unmount — rời trang là
//      quay lại mặc định, không rớt lại giá trị cũ.
//   2. Trang không gọi hook đó thì dùng mặc định của app, tức hành vi y hệt
//      trước khi có file này. Không có trạng thái "nửa vời".
//
// Bản ghi có `currency` RIÊNG (ReturnRequest, Dispute, PaymentTransaction...)
// thì ưu tiên dùng của bản ghi: `money.format(x, { currency: row.currency })`.
// Scope chỉ để lấp chỗ dữ liệu không mang tiền tệ.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  APP_CURRENCY,
  formatMoney,
  formatMoneyCompact,
  formatMoneySigned,
  currencySymbol,
} from '../utils/currency';

// ---- Store ngoài React ----
// Giữ ngoài component để `useCurrencyScope` set được từ effect của trang mà
// không phải nâng state lên gốc cây, và để nhiều subscriber cùng nhận một giá trị.
let scopeCurrency: string = APP_CURRENCY;
const listeners = new Set<() => void>();

function setScopeCurrency(code: string | null | undefined): void {
  const next = code?.trim().toUpperCase() || APP_CURRENCY;
  if (next === scopeCurrency) return;
  scopeCurrency = next;
  listeners.forEach((notify) => notify());
}

const CurrencyContext = createContext<string>(APP_CURRENCY);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrency] = useState<string>(scopeCurrency);

  useEffect(() => {
    const notify = () => setCurrency(scopeCurrency);
    listeners.add(notify);
    // Đồng bộ ngay lần mount: scope có thể đã bị set trước khi provider render.
    notify();
    return () => {
      listeners.delete(notify);
    };
  }, []);

  return <CurrencyContext.Provider value={currency}>{children}</CurrencyContext.Provider>;
}

type MoneyFormatOptions = {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /** Ghi đè cho bản ghi có tiền tệ riêng. */
  currency?: string | null;
};

/**
 * Formatter đã bind tiền tệ của phạm vi đang xem.
 * Dùng thay cho `import { formatMoney }` trong component hiển thị.
 */
export function useMoney() {
  const scope = useContext(CurrencyContext);

  return useMemo(
    () => ({
      currency: scope,
      symbol: currencySymbol(scope),
      format: (value: number, options?: MoneyFormatOptions) =>
        formatMoney(value, { currency: scope, ...options }),
      compact: (value: number, fractionDigits = 1, currency?: string | null) =>
        formatMoneyCompact(value, fractionDigits, currency ?? scope),
      signed: (value: number, options?: MoneyFormatOptions) =>
        formatMoneySigned(value, { currency: scope, ...options }),
    }),
    [scope]
  );
}

/**
 * Đặt tiền tệ cho phạm vi của trang. Gọi ở TRANG, không gọi ở component con.
 * Tự trả về mặc định khi rời trang.
 */
export function useCurrencyScope(code: string | null | undefined): void {
  const setScope = useCallback(setScopeCurrency, []);

  useEffect(() => {
    setScope(code);
    return () => setScope(null);
  }, [code, setScope]);
}
